import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { sendFirebasePasswordResetEmail } from '../services/firebasePassword';
import { firebaseAuth } from '../firebase';
import { orderService } from '../services/orderService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { config } from '../utils/config';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateProfile: updateUserInContext } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'address', 'password', 'orders'
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    country: 'RDC',
    postalCode: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, [user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      if (user) {
        const userData = user;
        setProfileData({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
        });
        setAddressData({
          street: userData.address?.street || '',
          city: userData.address?.city || '',
          country: userData.address?.country || 'RDC',
          postalCode: userData.address?.postalCode || '',
        });
      }
      
      // Récupérer les commandes
      const userId = user?.id || localStorage.getItem('userId');
      if (userId) {
        const ordersResponse = await orderService.getUserOrders(userId);
        if (ordersResponse.success) {
          setOrders(ordersResponse.orders || []);
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await updateUserInContext(profileData);
      if (response?.success) {
        setSuccess('Profil mis à jour avec succès');
      } else {
        setError(response?.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await updateUserInContext({ address: addressData });
      if (response?.success) {
        setSuccess('Adresse mise à jour avec succès');
      } else {
        setError(response?.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setSaving(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setSaving(false);
      return;
    }

    const fbUser = firebaseAuth?.currentUser;
    if (!fbUser?.email) {
      setError(
        'Session Firebase inactive. Déconnectez-vous puis reconnectez-vous, ou utilisez « Mot de passe oublié » sur la page de connexion.'
      );
      setSaving(false);
      return;
    }

    try {
      const cred = EmailAuthProvider.credential(
        fbUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(fbUser, cred);
      await updatePassword(fbUser, passwordData.newPassword);
      setSuccess('Mot de passe Firebase mis à jour. Utilisez-le pour vos prochaines connexions.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      const code = err?.code;
      const map = {
        'auth/wrong-password': 'Mot de passe actuel incorrect.',
        'auth/weak-password': 'Mot de passe trop faible.',
        'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard.',
        'auth/requires-recent-login': 'Reconnectez-vous puis réessayez.',
      };
      setError(map[code] || err?.message || 'Impossible de modifier le mot de passe.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendResetLink = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    const email = profileData.email?.trim();
    if (!email) {
      setError('Email du profil introuvable.');
      setSaving(false);
      return;
    }
    const result = await sendFirebasePasswordResetEmail(email);
    setSaving(false);
    if (result.success) {
      setSuccess(
        'Si un compte existe pour cet email, vous recevrez le lien de réinitialisation Firebase (vérifiez les spams).'
      );
    } else {
      setError(result.error || 'Envoi impossible.');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <div className="container">
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h1 className="dashboard-title" style={{ marginBottom: '30px' }}>
            👤 Mon Profil
          </h1>

          {/* Messages d'erreur/succès */}
          {error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              color: '#c33',
              marginBottom: '20px',
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              padding: '15px',
              backgroundColor: '#dfd',
              border: '1px solid #cfc',
              borderRadius: '8px',
              color: '#3c3',
              marginBottom: '20px',
            }}>
              {success}
            </div>
          )}

          {/* Onglets */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '30px',
            borderBottom: '2px solid #e0e0e0',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={() => setActiveTab('profile')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'profile' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'profile' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'profile' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              📝 Informations personnelles
            </button>
            <button
              onClick={() => setActiveTab('address')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'address' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'address' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'address' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              📍 Adresse de livraison
            </button>
            <button
              onClick={() => setActiveTab('password')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'password' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'password' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'password' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              🔒 Mot de passe
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'orders' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'orders' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'orders' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              📦 Mes Commandes ({orders.length})
            </button>
          </div>

          {/* Contenu des onglets */}
          {activeTab === 'profile' && (
            <div className="card">
              <h2 style={{ marginBottom: '20px', color: '#333' }}>Informations personnelles</h2>
              <form onSubmit={handleSaveProfile}>
                <div className="form-group">
                  <label htmlFor="firstName">Prénom *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Nom *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profileData.email}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#666' }}>L'email ne peut pas être modifié</small>
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    placeholder={config.CONTACT_PHONES[0]?.display || '+243 …'}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'address' && (
            <div className="card">
              <h2 style={{ marginBottom: '20px', color: '#333' }}>Adresse de livraison</h2>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Cette adresse sera utilisée par défaut pour vos commandes.
              </p>
              <form onSubmit={handleSaveAddress}>
                <div className="form-group">
                  <label htmlFor="street">Rue et numéro</label>
                  <input
                    type="text"
                    id="street"
                    name="street"
                    value={addressData.street}
                    onChange={handleAddressChange}
                    placeholder="Ex: Avenue X, N°123"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="city">Ville</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={addressData.city}
                      onChange={handleAddressChange}
                      placeholder="Ex: Kinshasa"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="postalCode">Code postal</label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={addressData.postalCode}
                      onChange={handleAddressChange}
                      placeholder="Ex: 001"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="country">Pays</label>
                  <select
                    id="country"
                    name="country"
                    value={addressData.country}
                    onChange={handleAddressChange}
                  >
                    <option value="RDC">République Démocratique du Congo</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer l\'adresse'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="card">
              <h2 style={{ marginBottom: '20px', color: '#333' }}>Mot de passe (Firebase)</h2>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '15px' }}>
                Votre mot de passe est celui de votre compte Firebase. Vous pouvez le modifier ici en indiquant
                l’ancien et le nouveau, ou recevoir un lien sécurisé par email (comme sur la page de connexion).
              </p>
              <form onSubmit={handleChangePassword}>
                <div className="form-group">
                  <label htmlFor="currentPassword">Mot de passe actuel *</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword">Nouveau mot de passe *</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                  />
                  <small style={{ color: '#666' }}>Minimum 6 caractères</small>
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Modification...' : 'Enregistrer le nouveau mot de passe'}
                </button>
              </form>
              <hr style={{ margin: '28px 0', borderColor: '#e0e0e0' }} />
              <p style={{ color: '#555', marginBottom: '12px', fontSize: '14px' }}>
                Vous ne vous souvenez pas du mot de passe actuel ? Demandez un lien de réinitialisation sur votre email
                Maxfly ({profileData.email || '—'}).
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={saving || !profileData.email}
                onClick={handleSendResetLink}
              >
                Recevoir un lien par email
              </button>
            </div>
          )}

          {activeTab === 'orders' && (
            <div>
              <h2 style={{ marginBottom: '20px', color: '#333' }}>Historique des commandes</h2>
              {orders.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666' }}>Aucune commande pour le moment</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/order')}
                    style={{ marginTop: '15px' }}
                  >
                    Créer ma première commande
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {orders.map((order) => (
                    <div key={order._id} className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: '0 0 10px 0', color: '#FF0000' }}>
                            Commande #{order._id.slice(-8).toUpperCase()}
                          </h3>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Plateforme:</strong> {order.platform}
                          </p>
                          {order.verifiedProductName && (
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>
                              <strong>Produit:</strong> {order.verifiedProductName}
                            </p>
                          )}
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Statut:</strong> {order.status}
                          </p>
                          {order.totalAmount && (
                            <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: 'bold', color: '#FF0000' }}>
                              <strong>Total:</strong> ${order.totalAmount.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div>
                          <button
                            className="btn btn-secondary"
                            onClick={() => navigate(`/tracking/${order._id}`)}
                          >
                            Voir détails
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;

